import React, { useEffect, useMemo, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Layout } from '@components';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';
import { normalizeUrl } from '@utils/url';

const StyledTableContainer = styled.div`
  margin: 100px -20px;

  @media (max-width: 768px) {
    margin: 50px -10px;
  }

  header {
    width: 100%;
  }

  .intro {
    margin-top: 10px;
    max-width: 620px;
  }

  table {
    width: 100%;
    border-collapse: collapse;

    .hide-on-mobile {
      @media (max-width: 768px) {
        display: none;
      }
    }

    tbody tr {
      &:hover,
      &:focus {
        background-color: var(--light-navy);
      }
    }

    th,
    td {
      padding: 10px;
      text-align: left;

      &:first-child {
        padding-left: 20px;

        @media (max-width: 768px) {
          padding-left: 10px;
        }
      }
      &:last-child {
        padding-right: 20px;

        @media (max-width: 768px) {
          padding-right: 10px;
        }
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }

    tr {
      cursor: default;

      td:first-child {
        border-top-left-radius: var(--border-radius);
        border-bottom-left-radius: var(--border-radius);
      }
      td:last-child {
        border-top-right-radius: var(--border-radius);
        border-bottom-right-radius: var(--border-radius);
      }
    }

    td {
      &.year {
        padding-right: 20px;

        @media (max-width: 768px) {
          padding-right: 10px;
          font-size: var(--fz-sm);
        }
      }

      &.title {
        padding-top: 15px;
        padding-right: 20px;
        color: var(--lightest-slate);
        font-size: var(--fz-xl);
        font-weight: 600;
        line-height: 1.25;
      }

      &.company {
        font-size: var(--fz-lg);
        white-space: nowrap;
      }

      &.tech {
        font-size: var(--fz-xxs);
        font-family: var(--font-mono);
        line-height: 1.5;
        .separator {
          margin: 0 5px;
        }
        span {
          display: inline-block;
        }
      }

      &.links {
        min-width: 100px;

        div {
          display: flex;
          align-items: center;

          a {
            ${({ theme }) => theme.mixins.flexCenter};
            flex-shrink: 0;
          }

          a + a {
            margin-left: 10px;
          }
        }
      }
    }
  }
`;

const StyledFilters = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 25px 0 0;
  justify-content: flex-start;
`;

const StyledFilterButton = styled.button`
  ${({ theme }) => theme.mixins.smallButton};
  padding: 10px 14px;
  background-color: transparent;
  color: ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--light-slate)')};
  border-color: ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--light-slate)')};

  &:hover,
  &:focus {
    background-color: var(--lightest-navy);
    color: var(--green);
    border-color: var(--green);
  }
`;

const ArchivePage = ({ location, data }) => {
  const all = useMemo(() => (data.allMarkdownRemark.edges || []).filter(({ node }) => node), [
    data.allMarkdownRemark.edges,
  ]);

  const categoryOptions = useMemo(() => {
    const categories = new Set();
    all.forEach(({ node }) => {
      const raw = node.frontmatter?.category;
      if (!raw) {
        return;
      }
      String(raw)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .forEach(c => categories.add(c));
    });
    return ['All', ...Array.from(categories)];
  }, [all]);

  const [activeCategory, setActiveCategory] = useState('All');

  const projects = useMemo(() => {
    const filtered =
      activeCategory === 'All'
        ? all
        : all.filter(({ node }) => {
          const raw = node.frontmatter?.category;
          if (!raw) {
            return false;
          }
          return String(raw)
            .split(',')
            .map(s => s.trim())
            .includes(activeCategory);
        });

    return [...filtered].sort((a, b) => {
      const aDate = a?.node?.frontmatter?.date ? new Date(a.node.frontmatter.date).getTime() : null;
      const bDate = b?.node?.frontmatter?.date ? new Date(b.node.frontmatter.date).getTime() : null;
      const aMs = Number.isFinite(aDate) ? aDate : -Infinity;
      const bMs = Number.isFinite(bDate) ? bDate : -Infinity;
      if (aMs !== bMs) {
        return bMs - aMs;
      } // date DESC
      return String(a?.node?.frontmatter?.title || '').localeCompare(
        String(b?.node?.frontmatter?.title || ''),
      );
    });
  }, [activeCategory, all]);

  const revealTitle = useRef(null);
  const revealTable = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealTable.current, srConfig(200, 0));
  }, []);

  return (
    <Layout location={location}>
      <Helmet
        title="Archive"
        meta={[
          {
            name: 'description',
            content:
              'Project archive of additional work including experiments and non-featured builds.',
          },
        ]}
      />

      <main>
        <header ref={revealTitle}>
          <h1 className="big-heading">Archive</h1>
          <p className="intro">A complete list of my non-featured projects.</p>

          {categoryOptions.length > 1 && (
            <StyledFilters aria-label="Archive filters">
              {categoryOptions.map(category => (
                <li key={category}>
                  <StyledFilterButton
                    type="button"
                    isActive={activeCategory === category}
                    onClick={() => setActiveCategory(category)}>
                    {category}
                  </StyledFilterButton>
                </li>
              ))}
            </StyledFilters>
          )}
        </header>

        <StyledTableContainer ref={revealTable}>
          <table>
            <thead>
              <tr>
                <th>Year</th>
                <th>Title</th>
                <th className="hide-on-mobile">Built with</th>
                <th>Link</th>
              </tr>
            </thead>
            <tbody>
              {projects.length > 0 &&
                projects.map(({ node }, i) => {
                  const { date, github, external, ios, android, title, tech } = node.frontmatter;
                  const externalUrl = normalizeUrl(external);
                  const githubUrl = normalizeUrl(github);
                  const iosUrl = normalizeUrl(ios);
                  const androidUrl = normalizeUrl(android);
                  return (
                    <tr key={i}>
                      <td className="overline year">{`${new Date(date).getFullYear()}`}</td>

                      <td className="title">{title}</td>

                      <td className="tech hide-on-mobile">
                        {tech?.length > 0 &&
                          tech.map((item, i) => (
                            <span key={i}>
                              {item}
                              {''}
                              {i !== tech.length - 1 && <span className="separator">&middot;</span>}
                            </span>
                          ))}
                      </td>

                      <td className="links">
                        <div>
                          {externalUrl && (
                            <a href={externalUrl} aria-label="External Link">
                              <Icon name="External" />
                            </a>
                          )}
                          {githubUrl && (
                            <a href={githubUrl} aria-label="GitHub Link">
                              <Icon name="GitHub" />
                            </a>
                          )}
                          {iosUrl && (
                            <a href={iosUrl} aria-label="Apple App Store Link">
                              <Icon name="AppStore" />
                            </a>
                          )}
                          {androidUrl && (
                            <a href={androidUrl} aria-label="Google Play Store Link">
                              <Icon name="PlayStore" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </StyledTableContainer>
      </main>
    </Layout>
  );
};
ArchivePage.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
};

export default ArchivePage;

export const pageQuery = graphql`
  {
    allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/content/user/projects/" } }
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      edges {
        node {
          frontmatter {
            date
            title
            order
            tech
            github
            external
            ios
            android
            category
          }
          html
        }
      }
    }
  }
`;
